import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import { format } from "date-fns";

import { Modal } from "@/components";

interface CutoffModalProps {
	cutoffDate: string;
	setCutoffDate: (value: string) => void;
	hasStopCampaignAt: boolean;
	onClose: () => void;
	onSave: () => void;
	onRemove: () => void;
}

// "Stop sending after date" modal for the currently-open lead — a single-date Datepicker
// extracted from ReadyToSend to keep that page under the file-length cap.
export const CutoffModal: FunctionComponent<CutoffModalProps> = ({
	cutoffDate,
	setCutoffDate,
	hasStopCampaignAt,
	onClose,
	onSave,
	onRemove,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.${key}`);

	return (
		<Modal
			clearButtonText={ts("removeCutoff")}
			disableSave={!cutoffDate}
			handleClear={hasStopCampaignAt ? onRemove : undefined}
			handleClose={onClose}
			handleSave={onSave}
			isOpened={true}
			overflow={true}
			size="sm"
			submitButtonText={t("basics.save")}
			subtitle={ts("stopSendingAfterDateSubtitle")}
			title={ts("stopSendingAfterDate")}
		>
			<div className="flex flex-col gap-2">
				<label className="text-sm font-semibold text-text-meta" htmlFor="cutoffDate">
					{ts("stopSendingAfter")}
				</label>
				<Datepicker
					asSingle={true}
					containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
					inputClassName="relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
					minDate={new Date()}
					placeholder={ts("selectDate")}
					separator="-"
					useRange={false}
					value={
						cutoffDate
							? {
									startDate: new Date(cutoffDate),
									endDate: new Date(cutoffDate),
								}
							: { startDate: null, endDate: null }
					}
					onChange={(value: DateValueType) =>
						setCutoffDate(value?.startDate ? format(value.startDate, "yyyy-MM-dd") : "")
					}
				/>
			</div>
		</Modal>
	);
};
